import { createPopper } from '@popperjs/core'
import excelReader from 'read-excel-file/web-worker'
import FUSEJS from 'fuse.js'
import MOMENT from 'moment'

export default {

  methods: {

    isMobile () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },

    scaleOnMobile (a, b) {
      if (this.isMobile()) {
        return Number(a.mobile) - Number(b.mobile)
      }
      return Number(b.index) - Number(a.index)
    },

    removeSelectedOnMobile (row, _key) {
      return this.isMobile() ? row.key !== 'selected' : true
    },

    tableRowClass (row, selector) {
      if (row && selector === 'row') {
        return row.last_seen ? 'table-seen-request' : 'table-unseen-request'
      }
      return ''
    },

    tableRowNumber (table, index) {
      return Number(table.currentPage) * Number(table.perPage) - Number(table.perPage) + (Number(index) + 1)
    },

    iconState (icon, isActive) {
      return `fa-xl ${isActive ? 'fad' : 'fat'} ${icon} mr-1`
    },

    statusByVariant (status = 'Pending', variant = null) {
      switch (status) {
        case 'Pending': {
          return variant ? `${variant}-dark` : 'dark'
        }
        case 'Processing': {
          return variant ? `${variant}-warning` : 'warning'
        }
        case 'Disapproved': {
          return variant ? `${variant}-danger` : 'danger'
        }
        case 'Canceled': {
          return variant ? `${variant}-danger` : 'danger'
        }
        default: {
          return variant ? `${variant}-success` : 'success'
        }
      }
    },

    generateId (length) {
      const numbers = '1234567890'
      const lettersUppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const lettersLowercase = 'abcdefghijklmnopqrstuvwxyz'
      const characters = `${numbers}${lettersUppercase}${lettersLowercase}`

      let result = ''

      const charactersLength = characters.length

      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }

      return result
    },

    swalConfirm (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        html: 'Save this Record?',
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        showDenyButton: false,
        denyButtonText: 'No',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        heightAuto: false,
        buttonsStyling: false,
        allowOutsideClick: false,
        width: '25rem'
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          denyButton: 'btn btn-sm btn-secondary ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    swalConfirmWithRemarks (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        input: 'textarea',
        inputLabel: 'Remarks',
        inputAutoFocus: true,
        width: '25rem',
        inputAttributes: {
          rows: 10,
          name: 'remarks',
          autocapitalize: 'off',
          maxlength: options?.remarksMaxLength || 2500,
          placeholder: options?.remarksRequired ? 'enter remarks' : 'enter remarks ( optional )'
        },
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Discard',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        buttonsStyling: false,
        heightAuto: false,
        allowOutsideClick: false,
        preConfirm: remarks => {
          if (typeof options?.preConfirm === 'function') {
            this.$swal.disableInput()
            return options?.preConfirm(remarks)?.finally(() => {
              this.$swal.enableInput()
            })
          }
        },
        inputValidator: remarks => {
          return new Promise(resolve => {
            if (options?.remarksMaxLength) {
              if (remarks?.length > Number(options?.remarksMaxLength)) {
                return resolve(
                  `The remarks field must be atleast lesser than or equal to ${options?.remarksMaxLength} characters`
                )
              }
            }

            if (options?.remarksRequired) {
              if (remarks?.length <= 0) {
                return resolve(
                  'The remarks field is required'
                )
              }
            }

            return resolve()
          })
        },
        didOpen: () => {
          const textArea = document.getElementById('swal2-input')
          if (textArea) {
            textArea.onkeydown = element => {
              const remarks = element.target.value

              if (options?.remarksMaxLength) {
                if (remarks?.search(/\n/g)) {
                  element.target.value = remarks.replace(/\n/g, '')
                }
              }
            }

            textArea.onchange = element => {
              const remarks = element.target.value
              if (remarks?.length > 0) {
                this.$swal.resetValidationMessage()
              }
            }
          }
        }
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          input: 'swal-input my-1 mx-3',
          inputLabel: 'd-flex align-self-start justify-content-start font-weight-bold col-12 px-3',
          validationMessage: 'align-self-start justify-content-start bg-white px-2 my-0 text-danger',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    swalSuccess (html = 'Record has been saved', title = 'Successful') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-success border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-primary'
        },
        width: '25rem'
      })
    },

    swalInvalid (html = '<h6>Your inputs seem to be incorrect or incomplete, please check and try again.</h6>', title = 'Validation Error') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-danger border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-danger'
        },
        width: '25rem'
      })
    },

    calculateDropPosition (dropDownList, component, { width }) {
      dropDownList.style.width = width
      const popper = createPopper(component.$refs.toggle, dropDownList, {
        // placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top'
              )
            }
          }
        ]
      })
      return () => popper.destroy()
    },

    fuseFilter (search = '', searchableOptions = [], config = {}) {
      const defaultConfig = {
        keys: [],
        shouldSort: true,
        threshold: 0.3
      }

      const fuse = new FUSEJS(
        searchableOptions,
        Object.assign(defaultConfig, config)
      )

      if (String(search).length) {
        return fuse.search(search).map(
          ({ item }) => item
        )
      }

      return fuse?.list || []
    },

    preventEnterKey (event) {
      if (event.keyCode === 13) {
        event.preventDefault()
      }
    },

    validateImportedCards (excelFile) {
      // card_number, card_pin, expiry_date, package_code

      return new Promise((resolve, reject) => {
        const dateFormat = 'YYYY-MM-DD'

        const schema = {
          'Card Number': {
            prop: 'card_number',
            type: String,
            required: true
          },
          'Card Pin': {
            prop: 'card_pin',
            type: String,
            required: true
          },
          'Expiration Date': {
            prop: 'expiration_date',
            type: originalDate => {
              const validatedDate = MOMENT(originalDate, dateFormat, true)
              if (validatedDate.isValid()) {
                if (validatedDate.isBefore(MOMENT())) {
                  throw new Error('Expiration Date must be greater than today')
                }
              } else {
                throw new Error(`Invalid Expiration Date Format, Format must be ${dateFormat}`)
              }

              return validatedDate.format(dateFormat).toString()
            },
            required: true
          },
          'Package Code': {
            prop: 'package_code',
            type: String,
            required: true
          },
          Amount: {
            prop: 'amount',
            type: Number
          }
        }

        excelReader(excelFile, { schema, dateFormat }).then(
          validatedData => {
            const { rows, errors } = validatedData
            if (rows.length <= 0) {
              this.swalInvalid(
              `
                  <div class="text-left">
                    No valid Data found in the uploaded file.
                    <br /><br />Please ensure all rows adhere to the required format:
                    <br /><br />A. Card Number (text)
                    <br />B. Card Pin (text)
                    <br />C. Expiration Date (YYYY-MM-DD, future date)
                    <br />D. Package Code (text)
                    <br />E. Amount (number, optional)
                  </div>
                `,
              'Invalid Data Format'
              )
            }

            if (errors.length) {
              this.swalInvalid(
                `
                  <div class="text-left">
                    The uploaded file contains errors.
                    <br /><br />Review the <strong class="text-danger">Invalid Imports</strong> section for details and correct the errors before retrying and ensure all rows adhere to the required format:
                    <br /><br />A. Card Number (text)
                    <br />B. Card Pin (text)
                    <br />C. Expiration Date (YYYY-MM-DD, future date)
                    <br />D. Package Code (text)
                    <br />E. Amount (number, optional)
                  </div>
                `,
                'Invalid Data Format'
              )
            }

            resolve(validatedData)
          }
        ).catch(() => {
          this.swalInvalid(
            `
              <div class="text-left">
                Please upload a valid Excel file (.xls or .xlsx)
                <br /><br />Please ensure all headers adhere to the required format:
                <br /><br />A. Card Number  (text)
                <br />B. Card Pin (text)
                <br />C. Expiration Date (YYYY-MM-DD, future date)
                <br />D. Package Code (text)
                <br />E. Amount (number, optional)
              </div>
            `,
            'Invalid File Format'
          )
          reject(new Error('Invalid File Format'))
        })
      })
    }
  }
}
